/**
 * 房内聊天 store (M10).
 *
 * 数据流:
 * - WS room_state 到达 → applyHistory(frame.chat_history) 整体替换
 * - WS event(chat_message) → addIncoming(record) 增量追加 + 触发头像气泡
 * - 用户打开聊天面板 → markRead(): 清空未读计数
 * - 离开房间 → reset()
 *
 * 容量约束 (跟 backend 一致): 历史最多 50 条, 超出 shift 旧的.
 *
 * 头像气泡 (recentBubbles):
 *   每收到一条新消息, 给发送者 pid 设一个 bubble entry, 标记 expiresAt =
 *   now + 3000ms. Seat 组件根据 store 读 bubble[pid], 配合 setTimeout 自动
 *   过期清理. id 字段用来在快速连发时让 Vue Transition 重置动画 (新 id
 *   = 新 DOM 节点).
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ChatMessage } from '@/ws/frames'

export interface BubbleEntry {
  id:        number              // 每次新消息单调递增, 用于 Transition key
  message:   string
  name:      string
  expiresAt: number              // performance.now() + 3000
}

const HISTORY_CAP   = 50
const BUBBLE_TTL_MS = 3000


export const useChatStore = defineStore('chat', () => {
  /** 全部消息, 时间升序 (新消息 push 到末尾). */
  const messages = ref<ChatMessage[]>([])

  /** 面板关闭时累加的未读数. 打开面板 → markRead 清零. */
  const unread = ref<number>(0)

  /** pid -> 当前应在头像旁显示的 bubble. 过期后该 key 被删除. */
  const bubbles = ref<Map<string, BubbleEntry>>(new Map())

  /** 面板当前是否打开. 用于 addIncoming 时决定是否计未读. */
  const panelOpen = ref<boolean>(false)

  let bubbleSeq = 0

  // ── 派生 ──────────────────────────────────────────────────────

  /** 最新消息 (用于关闭面板时在按钮上预览). */
  const latest = computed<ChatMessage | null>(() =>
    messages.value.length ? messages.value[messages.value.length - 1] : null,
  )

  // ── 写入 ──────────────────────────────────────────────────────

  /** room_state 整体同步. 重置 unread (snapshot 视为已对齐). */
  function applyHistory(history: ChatMessage[] | undefined | null): void {
    messages.value = (history || []).slice(-HISTORY_CAP)
    unread.value = 0
    // 不清 bubbles — room_state 频繁触发 (亮牌/行动等), 清会打断正在显示的气泡.
    // 气泡依赖自身 TTL 过期 (3s). 重连时 reset() 才做整体清理.
  }

  /** 增量收到一条新消息 (kind=chat_message event). */
  function addIncoming(msg: ChatMessage): void {
    messages.value.push(msg)
    if (messages.value.length > HISTORY_CAP) {
      messages.value.splice(0, messages.value.length - HISTORY_CAP)
    }
    if (!panelOpen.value) unread.value += 1

    // 头像气泡: 新 id 让 Transition 重新触发动画.
    const id = ++bubbleSeq
    const entry: BubbleEntry = {
      id,
      message:   msg.message,
      name:      msg.name,
      expiresAt: performance.now() + BUBBLE_TTL_MS,
    }
    bubbles.value.set(msg.pid, entry)
    setTimeout(() => {
      const cur = bubbles.value.get(msg.pid)
      if (cur && cur.id === id) {
        // 还没被更新的消息覆盖 — 自然过期清理
        const m = new Map(bubbles.value)
        m.delete(msg.pid)
        bubbles.value = m
      }
    }, BUBBLE_TTL_MS + 50)
  }

  /** 打开面板时调用, 清空未读. */
  function markRead(): void {
    unread.value = 0
  }

  function setPanelOpen(open: boolean): void {
    panelOpen.value = open
    if (open) unread.value = 0
  }

  function reset(): void {
    messages.value = []
    unread.value = 0
    bubbles.value = new Map()
    panelOpen.value = false
  }

  return {
    // state
    messages, unread, bubbles, panelOpen,
    // derived
    latest,
    // mutations
    applyHistory, addIncoming, markRead, setPanelOpen, reset,
  }
})
